import { GoogleDrivePicker } from "../google-drive-picker";
import { DocSendPicker } from "@/components/common/docsend-picker";
import { SpreadsheetViewer } from "@/components/common/spreadsheet-viewer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  StepCard as Card,
  StepCardContent as CardContent,
  StepCardDescription as CardDescription,
  StepCardHeader as CardHeader,
  StepCardTitle as CardTitle,
} from "../portal-ui";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
// Tabs imports removed as they are no longer used
import { axiosInstance } from "@/lib/axios-instance";
import { isDevS3File } from "@/lib/url-utils";
import { cn, FILE_TYPE_MAPPING } from "@/lib/utils";
import {
  Check,
  CheckCircle2,
  ChevronsUpDown,
  ExternalLink,
  Eye,
  FileChartPie,
  FileImage,
  FileSpreadsheet,
  FileText,
  History,
  Loader2,
  Trash2,
  Upload,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

interface CompanyData {
  _id: string;
}

const GoogleDriveIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 87.3 78"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z"
      fill="#0066da"
    />
    <path
      d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z"
      fill="#00ac47"
    />
    <path
      d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z"
      fill="#ea4335"
    />
    <path
      d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z"
      fill="#00832d"
    />
    <path
      d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z"
      fill="#2684fc"
    />
    <path
      d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z"
      fill="#ffba00"
    />
  </svg>
);

const DocSendIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <rect width="24" height="24" rx="4" fill="#5D5FEF" />
    <path
      d="M7 7h10v2H7V7zm0 4h10v2H7v-2zm0 4h7v2H7v-2z"
      fill="white"
    />
  </svg>
);

const MAX_DOCUMENTS = 10;
const MANDATORY_DOCUMENTS = ["Pitch Deck"];

const formatDate = (dateStr: string | Date) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// File type icons stay neutral: the type is in the shape of the icon and the
// file name, and colour here would compete with the one accent on the page.
const NEUTRAL_FILE = { color: "text-muted-foreground", bgColor: "bg-muted" };
const FILE_ICONS = {
  pdf: { icon: FileText, ...NEUTRAL_FILE },
  pptx: { icon: FileChartPie, ...NEUTRAL_FILE },
  csv: { icon: FileSpreadsheet, ...NEUTRAL_FILE },
  png: { icon: FileImage, ...NEUTRAL_FILE },
  jpeg: { icon: FileImage, ...NEUTRAL_FILE },
  jpg: { icon: FileImage, ...NEUTRAL_FILE },
};

interface Document {
  file?: File;
  label: string;
  file_type: string;
  existing: boolean;
  uploaded: boolean;
  _id?: string;
  file_url?: string;
  file_name?: string;
  upload_source?: "local" | "google_drive" | "docsend";
  time_stamp?: string;
}

interface ImportedFile {
  _id: string;
  file_type: string;
  file_url?: string;
  file_name?: string;
}

interface UploadDocumentsStepProps {
  companyData: CompanyData;
  opportunityId: string;
  onNext: () => void;
  onBack?: () => void;
}

export default function UploadDocumentsStep({
  companyData,
  opportunityId,
  onNext,
  onBack,
}: UploadDocumentsStepProps) {
  const [selectedLabel, setSelectedLabel] = useState("");
  const [otherLabel, setOtherLabel] = useState("");
  const [documentTypeOpen, setDocumentTypeOpen] = useState(false);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedMandatory, setSelectedMandatory] = useState<
    Record<string, boolean>
  >({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [filesToDelete, setFilesToDelete] = useState<Document[]>([]);
  const [fileToDelete, setFileToDelete] = useState<Document | null>(null);
  const [isGoogleDriveOpen, setIsGoogleDriveOpen] = useState(false);
  const [isDocSendOpen, setIsDocSendOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedFileHistory, setSelectedFileHistory] = useState<Document[]>(
    []
  );

  const handleShowHistory = (fileType: string) => {
    const filesOfThisType = documents.filter(
      (doc) => doc.file_type === fileType
    );
    // Sort by timestamp, newest first
    const sortedFiles = [...filesOfThisType].sort((a, b) => {
      const dateA = a.time_stamp
        ? new Date(a.time_stamp).getTime()
        : a.file
          ? 0
          : -1;
      const dateB = b.time_stamp
        ? new Date(b.time_stamp).getTime()
        : b.file
          ? 0
          : -1;
      return dateB - dateA;
    });
    setSelectedFileHistory(sortedFiles);
    setHistoryDialogOpen(true);
  };

  const getLabelFromFileType = (fileType: string) => {
    return (
      Object.keys(FILE_TYPE_MAPPING).find(
        (key) =>
          FILE_TYPE_MAPPING[key as keyof typeof FILE_TYPE_MAPPING] === fileType
      ) || fileType
    );
  };

  const fetchCompanyFiles = useCallback(async (companyId: string) => {
    try {
      const response = await axiosInstance.get(
        `/fetch_company_files?company_id=${companyId}`
      );

      if (response.data.files) {
        const existingDocs = response.data.files.map(
          (file: {
            file_type: string;
            _id: string;
            file_url: string;
            file_name?: string;
            upload_source?: "local" | "google_drive" | "docsend";
            time_stamp?: string;
          }) => ({
            file: undefined,
            label: getLabelFromFileType(file.file_type),
            existing: true,
            uploaded: true,
            _id: file._id,
            file_url: file.file_url,
            file_type: file.file_type,
            file_name: file.file_name,
            upload_source: file.upload_source || "local",
            time_stamp: file.time_stamp,
          })
        );
        setDocuments(existingDocs);
      }
    } catch (e: unknown) {
      const error = e as { response?: { data?: { message?: string } } };
      console.error(error as Error);
      // Only toast on error
      if (error && error.response) {
        toast.error(
          error.response?.data?.message || "Error fetching company files."
        );
      }
    }
  }, []); // Empty dependency array as getLabelFromFileType is constant-ish (defined outside or simple)

  useEffect(() => {
    console.log("Company ID:", companyData?._id);
    if (companyData?._id) {
      fetchCompanyFiles(companyData._id);
    } else {
      console.error("No company ID found in companyData");
    }
  }, [companyData?._id, fetchCompanyFiles]);

  const handleGoogleDriveImport = (importedFiles: ImportedFile[]) => {
    const newDocs = importedFiles.map((f) => ({
      file: undefined,
      label: getLabelFromFileType(f.file_type),
      file_type: f.file_type,
      existing: true,
      uploaded: true,
      _id: f._id,
      file_url: f.file_url,
      file_name: f.file_name,
      upload_source: "google_drive" as const,
    }));

    const uniqueNewDocs = newDocs.filter(
      (newDoc) => !documents.some((d) => d._id === newDoc._id)
    );

    setDocuments([...documents, ...uniqueNewDocs]);

    uniqueNewDocs.forEach((doc) => {
      if (MANDATORY_DOCUMENTS.includes(doc.label)) {
        setSelectedMandatory((prev) => ({ ...prev, [doc.label]: true }));
      }
    });

    setIsGoogleDriveOpen(false);
  };

  const handleDocSendImport = (importedFile: {
    _id: string;
    file_name: string;
    file_type: string;
    file_url: string;
    file_size: string;
  }) => {
    const newDoc = {
      file: undefined,
      label: getLabelFromFileType(importedFile.file_type),
      file_type: importedFile.file_type,
      existing: true,
      uploaded: true,
      _id: importedFile._id,
      file_url: importedFile.file_url,
      upload_source: "docsend" as const,
    };

    // Check if document already exists
    if (!documents.some((d) => d._id === newDoc._id)) {
      setDocuments([...documents, newDoc]);

      if (MANDATORY_DOCUMENTS.includes(newDoc.label)) {
        setSelectedMandatory((prev) => ({ ...prev, [newDoc.label]: true }));
      }
    }

    setIsDocSendOpen(false);
  };

  const handleLabelChange = (value: string) => {
    setSelectedLabel(value);
    if (value !== "Other") {
      setOtherLabel("");
    }
  };

  const handleFileSelection = (files: FileList | null) => {
    if (!files?.length) return;

    const file = files[0];
    const label = selectedLabel === "Other" ? otherLabel : selectedLabel;
    const fileType =
      selectedLabel === "Other"
        ? otherLabel
        : FILE_TYPE_MAPPING[label as keyof typeof FILE_TYPE_MAPPING] ||
        `company ${label}`;

    if (!label) {
      setErrors({ ...errors, label: "Please select a label first." });
      return;
    }

    const fileExtension = file.name.split(".").pop()?.toLowerCase() || "";
    const isImageFile = label === "Company Logo" || label === "Company Banner";
    const allowedExtensions = isImageFile
      ? ["png", "jpeg", "jpg"]
      : ["pdf", "pptx"];

    if (!allowedExtensions.includes(fileExtension)) {
      setErrors({
        ...errors,
        label: isImageFile
          ? `Only PNG, JPEG and JPG files are allowed. File '${file.name}' is not supported.`
          : `Only PDF and PPTX files are allowed. File '${file.name}' is not supported.`,
      });
      return;
    }

    // Allow versioning - remove duplicate check
    // Users can now upload multiple versions of the same document type

    if (documents.length < MAX_DOCUMENTS) {
      const newDocument: Document = {
        file,
        label,
        file_type: fileType,
        existing: false,
        uploaded: false,
        upload_source: "local",
      };
      setDocuments([...documents, newDocument]);

      if (MANDATORY_DOCUMENTS.includes(label)) {
        setSelectedMandatory((prev) => ({ ...prev, [label]: true }));
      }

      setErrors({ ...errors, limit: "" });
    } else {
      setErrors({
        ...errors,
        limit: `You can upload a maximum of ${MAX_DOCUMENTS} documents.`,
      });
    }
  };


  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelection(e.target.files);
    e.target.value = "";
  };

  const handleRemoveDocument = (index: number) => {
    const removedDoc = documents[index];
    const newDocuments = documents.filter((_, i) => i !== index);
    setDocuments(newDocuments);

    // Note: Existing files are now deleted immediately in the confirmation dialog
    // so we don't need to add them to filesToDelete array anymore

    if (MANDATORY_DOCUMENTS.includes(removedDoc.label)) {
      setSelectedMandatory((prev) => ({ ...prev, [removedDoc.label]: false }));
    }

    setErrors({ ...errors, limit: "" });
  };

  const uploadFile = async (
    file: File,
    companyId: string,
    label: string,
    fileType: string
  ) => {
    console.log("uploadFile called with:", { companyId, label, fileType });

    if (!companyId || companyId === "undefined") {
      console.error("Invalid company ID:", companyId);
      return false;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("file_name", label.toLowerCase().replace(/ /g, "_"));
    formData.append("file_type", fileType);
    formData.append("object_ref", "company");
    formData.append("object_ref_value", companyId);

    try {
      const response = await axiosInstance.post(
        `/upload_file_secure?company_id=${companyId}&pertains_to_opportunity=${opportunityId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.status === "success") {
        console.log(`Updated ${label} url: ${response.data.result.file_url}`);
        return true;
      } else {
        console.error("Error uploading file:", response.data);
        return false;
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      return false;
    }
  };

  const deleteFile = async (companyId: string, fileType: string) => {
    try {
      const response = await axiosInstance.delete(
        `/delete_company_file?company_id=${companyId}&file_type=${encodeURIComponent(
          fileType
        )}`
      );

      if (response.data.status === "success") {
        console.log(`Deleted ${fileType}`);
        return true;
      } else {
        console.error("Error deleting file:", response.data);
        return false;
      }
    } catch (error) {
      console.error("Error deleting file:", error);
      return false;
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setIsUploading(true);
    setUploadProgress(0);

    // Check mandatory documents
    const mandatoryMissing = MANDATORY_DOCUMENTS.some(
      (label) =>
        !documents.some(
          (doc) =>
            doc.file_type ===
            FILE_TYPE_MAPPING[label as keyof typeof FILE_TYPE_MAPPING] &&
            (doc.uploaded || selectedMandatory[label] || doc.file)
        )
    );

    if (mandatoryMissing) {
      setErrors({
        ...errors,
        mandatory: "Please upload all mandatory documents.",
      });
      setSubmitting(false);
      setIsUploading(false);
      return;
    }

    try {
      if (!companyData?._id) {
        console.error("Company data missing when submitting documents", {
          companyData,
          opportunityId,
        });
        toast.error(
          "We couldn't determine your company record yet. Please go back and confirm your basic details before uploading documents."
        );
        setSubmitting(false);
        setIsUploading(false);
        setUploadProgress(0);
        return;
      }

      let progress = 0;
      const totalOperations = documents.filter(
        (doc) => !doc.existing && !doc.uploaded
      ).length;
      const progressStep = totalOperations > 0 ? 100 / totalOperations : 100;

      // Delete files marked for deletion (fallback for any files that weren't deleted immediately)
      // This should typically be empty now since deletion happens immediately in the confirmation dialog
      for (const file of filesToDelete) {
        await deleteFile(companyData._id, file.file_type);
        progress += progressStep;
        setUploadProgress(Math.min(progress, 95));
      }

      // Upload new files
      const failedUploads: string[] = [];
      for (const document of documents) {
        if (!document.existing && !document.uploaded && document.file) {
          const success = await uploadFile(
            document.file,
            companyData._id,
            document.label,
            document.file_type
          );
          if (success) {
            document.uploaded = true;
          } else {
            failedUploads.push(document.label);
          }
          progress += progressStep;
          setUploadProgress(Math.min(progress, 95));
        }
      }

      // Don't advance on a silent upload failure — the success toast previously
      // fired regardless of the per-file result, so a failed pitch-deck upload
      // looked successful but never reached the deal.
      if (failedUploads.length > 0) {
        toast.error(
          `Failed to upload: ${failedUploads.join(", ")}. Please try again.`
        );
        setSubmitting(false);
        setIsUploading(false);
        setUploadProgress(0);
        return;
      }

      setUploadProgress(100);
      toast.success("Documents uploaded successfully");

      setTimeout(() => {
        setSubmitting(false);
        setIsUploading(false);
        setUploadProgress(0);
        onNext();
      }, 500);
    } catch (error) {
      console.error("Error submitting documents:", error);
      toast.error("Failed to upload documents");
      setSubmitting(false);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const isMandatoryUploaded = (label: string) => {
    return documents.some(
      (doc) =>
        doc.file_type ===
        FILE_TYPE_MAPPING[label as keyof typeof FILE_TYPE_MAPPING] &&
        (doc.uploaded || doc.file)
    );
  };

  const getFileExtension = (name: string) => {
    // Strip query params from URLs before extracting extension
    const clean = name.split("?")[0];
    return clean.split(".").pop()?.toLowerCase() || "";
  };

  // Check extension from file name, file_type field, AND the URL as fallback
  const matchesExtension = (
    extensions: string[],
    fileName: string,
    fileType?: string,
    fileUrl?: string
  ) => {
    const nameExt = getFileExtension(fileName);
    const type = fileType?.toLowerCase() || "";
    const urlExt = fileUrl ? getFileExtension(fileUrl) : "";
    return (
      extensions.includes(nameExt) ||
      extensions.includes(type) ||
      extensions.includes(urlExt)
    );
  };

  const isPDF = (fileName: string, fileType?: string, fileUrl?: string) => {
    return matchesExtension(["pdf"], fileName, fileType, fileUrl);
  };

  const isPowerPoint = (
    fileName: string,
    fileType?: string,
    fileUrl?: string
  ) => {
    return matchesExtension(["ppt", "pptx"], fileName, fileType, fileUrl);
  };

  const isImage = (fileName: string, fileType?: string, fileUrl?: string) => {
    return matchesExtension(
      ["png", "jpeg", "jpg", "ico"],
      fileName,
      fileType,
      fileUrl
    );
  };

  const isExcel = (fileName: string, fileType?: string, fileUrl?: string) => {
    return matchesExtension(["xlsx", "xls"], fileName, fileType, fileUrl);
  };

  const isCSV = (fileName: string, fileType?: string, fileUrl?: string) => {
    return matchesExtension(["csv"], fileName, fileType, fileUrl);
  };

  const getFileIcon = (doc: Document) => {
    const isImageFile =
      doc.label === "Company Logo" || doc.label === "Company Banner";
    if (isImageFile) return FILE_ICONS.png;
    const fileName = doc.file?.name || doc.file_name || "";
    const url = doc.file_url || "";
    if (isCSV(fileName, doc.file_type, url)) return FILE_ICONS.csv;
    if (isPowerPoint(fileName, doc.file_type, url)) return FILE_ICONS.pptx;
    return FILE_ICONS.pdf;
  };

  const getViewUrl = (doc: Document) => {
    if (doc.file_url) {
      return doc.file_url;
    }
    if (doc.file) {
      return URL.createObjectURL(doc.file);
    }
    return null;
  };

  const getFileName = (doc: Document) => {
    if (doc.file) {
      return doc.file.name;
    }
    if (doc.file_name) {
      return doc.file_name;
    }
    return `${doc.label}.pdf`; // Default for existing files
  };

  if (!companyData?._id) {
    return (
      <div className="mx-auto w-full max-w-2xl px-5 sm:px-8">
        <Card>
          <CardHeader>
            <CardTitle>Setting up your company</CardTitle>
            <p className="text-muted-foreground">
              We are finalizing your company profile before accepting document
              uploads. Please go back one step and make sure your basic
              information is saved.
            </p>
          </CardHeader>
          <CardContent>
            <Button onClick={onNext} variant="outline">
              Return to previous step
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-5 sm:px-8">
      <Card>
        <CardHeader>
          <CardTitle>Documents</CardTitle>
          <CardDescription>
            Your pitch deck is required. Anything else that helps the fund
            understand the company is welcome.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* What's required, ticked off as it arrives. */}
          <div className="rounded-xl border border-border bg-card px-4 py-4 sm:px-5">
            <p className="text-[13px] font-medium text-muted-foreground">
              Required
            </p>
            <ul className="mt-3 space-y-2.5">
              {MANDATORY_DOCUMENTS.map((label) => {
                const done = selectedMandatory[label] || isMandatoryUploaded(label);
                return (
                  <li key={label} className="flex items-center gap-2.5">
                    {done ? (
                      <CheckCircle2 className="h-[18px] w-[18px] text-foreground" />
                    ) : (
                      <span className="h-[18px] w-[18px] rounded-full border-[1.5px] border-input" />
                    )}
                    <span
                      className={cn(
                        "text-[15px]",
                        done ? "text-foreground" : "text-foreground/80"
                      )}
                    >
                      {label}
                    </span>
                  </li>
                );
              })}
            </ul>
            <p className="mt-4 border-t border-border/70 pt-3 text-[13px] leading-relaxed text-muted-foreground">
              Uploading a newer version of a document keeps the earlier ones, so
              you can update your deck at any time.
            </p>
          </div>

          {/* Upload: choose what the file is, then drop it in. Cloud imports sit
              underneath as a quieter alternative rather than competing cards. */}
          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="label-select">Document Type</Label>
              <Popover open={documentTypeOpen} onOpenChange={setDocumentTypeOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={documentTypeOpen}
                    className="w-full justify-between bg-card font-normal"
                    disabled={submitting}
                  >
                    {selectedLabel || "Select type..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[--radix-popover-trigger-width] p-0"
                  align="start"
                  // keep the list clear of the sticky step header (~56px) when it flips upward
                  collisionPadding={{ top: 64, bottom: 8 }}
                >
                  <Command className="max-h-[--radix-popover-content-available-height]">
                    <CommandInput placeholder="Search document type..." />
                    <CommandList>
                      <CommandEmpty>No document type found.</CommandEmpty>
                      <CommandGroup>
                        {Object.keys(FILE_TYPE_MAPPING)
                          .filter((label) => !["CSV", "XLSX"].includes(label))
                          .map((label) => (
                            <CommandItem
                              key={label}
                              value={label}
                              onSelect={() => {
                                handleLabelChange(label);
                                setDocumentTypeOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  selectedLabel === label ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {label}
                            </CommandItem>
                          ))}
                        <CommandItem
                          value="Other"
                          onSelect={() => {
                            handleLabelChange("Other");
                            setDocumentTypeOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              selectedLabel === "Other" ? "opacity-100" : "opacity-0"
                            )}
                          />
                          Other
                        </CommandItem>
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {errors.label && (
                <p className="text-sm text-destructive">{errors.label}</p>
              )}
            </div>

            {selectedLabel === "Other" && (
              <div className="space-y-2">
                <Label htmlFor="other-label">Custom Type Name</Label>
                <Input
                  id="other-label"
                  value={otherLabel}
                  onChange={(e) => setOtherLabel(e.target.value)}
                  placeholder="e.g. Legal Agreement"
                  className="bg-card"
                />
              </div>
            )}

            <div>
            <input
              id="file-upload"
              type="file"
              onChange={handleFileChange}
              disabled={
                !selectedLabel ||
                documents.length >= MAX_DOCUMENTS ||
                submitting
              }
              accept={
                selectedLabel === "Company Logo" ||
                  selectedLabel === "Company Banner"
                  ? ".png,.jpeg,.jpg"
                  : ".pdf,.pptx"
              }
              className="hidden"
            />
            <label
              htmlFor="file-upload"
              className={`flex flex-col items-center justify-center w-full h-36 border-[1.5px] border-dashed rounded-xl transition-colors duration-150 ${submitting || documents.length >= MAX_DOCUMENTS
                ? "border-input bg-muted cursor-not-allowed opacity-60"
                : !selectedLabel
                  ? "border-input bg-card/60 hover:border-foreground/30 cursor-pointer"
                  : "border-foreground/30 bg-card hover:border-foreground/60 cursor-pointer"
                }`}
              onDrop={(e) => {
                e.preventDefault();
                if (
                  selectedLabel &&
                  !submitting &&
                  documents.length < MAX_DOCUMENTS
                ) {
                  handleFileSelection(e.dataTransfer.files);
                } else if (!selectedLabel) {
                  setErrors({
                    ...errors,
                    label: "Please select a document type above first.",
                  });
                }
              }}
              onDragOver={(e) => e.preventDefault()}
              onDragEnter={(e) => {
                e.preventDefault();
                if (
                  selectedLabel &&
                  !submitting &&
                  documents.length < MAX_DOCUMENTS
                ) {
                  e.currentTarget.classList.remove("border-foreground/30");
                  e.currentTarget.classList.add("border-foreground");
                }
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                if (
                  selectedLabel &&
                  !submitting &&
                  documents.length < MAX_DOCUMENTS
                ) {
                  e.currentTarget.classList.remove("border-foreground");
                  e.currentTarget.classList.add("border-foreground/30");
                }
              }}
              onClick={(e) => {
                if (!selectedLabel) {
                  e.preventDefault();
                  setErrors({
                    ...errors,
                    label: "Please select a document type above first.",
                  });
                }
              }}
            >
              <div
                className={`mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg border ${selectedLabel
                  ? "border-border bg-background text-foreground"
                  : "border-border/70 bg-muted text-muted-foreground/70"
                  }`}
              >
                <Upload className="h-[18px] w-[18px]" />
              </div>
              <div className="text-center space-y-1">
                <p
                  className={`font-medium ${selectedLabel ? "text-foreground" : "text-muted-foreground"
                    }`}
                >
                  Drop a file here, or click to browse
                </p>
                <p className="text-xs text-muted-foreground">
                  {!selectedLabel
                    ? "Select type first"
                    : selectedLabel === "Company Logo" ||
                      selectedLabel === "Company Banner"
                      ? "PNG, JPG up to 10MB"
                      : "PDF, PPTX up to 25MB"}
                </p>
              </div>
            </label>
            {errors.duplicate && (
              <p className="text-sm text-destructive mt-2">
                {errors.duplicate}
              </p>
            )}
            {errors.limit && (
              <p className="text-sm text-destructive mt-2">{errors.limit}</p>
            )}
            </div>

            <div className="flex flex-col gap-2.5 border-t border-border/70 pt-5 sm:flex-row sm:items-center">
              <p className="text-[13px] text-muted-foreground sm:mr-auto">
                Or import from
              </p>
              <Button
                type="button"
                variant="outline"
                className="h-9 gap-2 rounded-lg bg-card"
                onClick={() =>
                  !submitting &&
                  documents.length < MAX_DOCUMENTS &&
                  setIsGoogleDriveOpen(true)
                }
                disabled={submitting || documents.length >= MAX_DOCUMENTS}
              >
                <GoogleDriveIcon className="h-4 w-4" />
                Google Drive
              </Button>
              <Button
                type="button"
                variant="outline"
                className="h-9 gap-2 rounded-lg bg-card"
                onClick={() =>
                  !submitting &&
                  documents.length < MAX_DOCUMENTS &&
                  setIsDocSendOpen(true)
                }
                disabled={submitting || documents.length >= MAX_DOCUMENTS}
              >
                <ExternalLink className="h-4 w-4" />
                DocSend link
              </Button>
            </div>
          </div>

          {/* Upload Progress */}
          {isUploading && (
            <div className="space-y-2">
              <Progress value={uploadProgress} />
              <p className="text-sm text-center text-muted-foreground">
                Uploading documents... {Math.round(uploadProgress)}%
              </p>
            </div>
          )}

          {/* Documents List */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[15px] font-semibold tracking-tight">
                Uploaded
              </h3>
              <span className="text-[13px] tabular-nums text-muted-foreground">
                {documents.length} of {MAX_DOCUMENTS}
              </span>
            </div>

            {documents.length === 0 ? (
              <div className="rounded-xl border-[1.5px] border-dashed border-border px-4 py-8 text-center text-[14px] text-muted-foreground">
                Nothing uploaded yet. Your pitch deck is the place to start.
              </div>
            ) : (
              <div className="space-y-3">
                {(() => {
                  // Helper to truncate file names
                  const truncateFileName = (name: string, maxLength = 18) => {
                    if (name.length <= maxLength) return name;
                    const extIndex = name.lastIndexOf(".");
                    if (extIndex === -1 || extIndex < name.length - 8) {
                      // No extension or extension is long, just truncate
                      return name.slice(0, maxLength - 3) + "...";
                    }
                    const ext = name.slice(extIndex);
                    const base = name.slice(0, maxLength - ext.length - 3);
                    return base + "..." + ext;
                  };

                  // Group documents by file_type
                  const documentsByType = documents.reduce((acc, doc) => {
                    if (!acc[doc.file_type]) {
                      acc[doc.file_type] = [];
                    }
                    acc[doc.file_type].push(doc);
                    return acc;
                  }, {} as Record<string, Document[]>);

                  // Get the latest version for each file type (newest timestamp first)
                  const latestDocuments = Object.entries(documentsByType).map(
                    ([, docs]) => {
                      const sorted = [...docs].sort((a, b) => {
                        const dateA = a.time_stamp
                          ? new Date(a.time_stamp).getTime()
                          : a.file
                            ? 0
                            : -1;
                        const dateB = b.time_stamp
                          ? new Date(b.time_stamp).getTime()
                          : b.file
                            ? 0
                            : -1;
                        return dateB - dateA;
                      });
                      return { ...sorted[0], versionCount: docs.length };
                    }
                  );

                  return latestDocuments.map((doc, index) => {
                    const filesOfThisType =
                      documentsByType[doc.file_type] || [];
                    const hasMultipleVersions = filesOfThisType.length > 1;
                    const fileConfig = getFileIcon(doc);
                    const Icon = fileConfig.icon;
                    const source =
                      doc.upload_source === "google_drive"
                        ? "google_drive"
                        : doc.upload_source === "docsend"
                          ? "docsend"
                          : "local";
                    const fileNameToShow = doc.file
                      ? truncateFileName(doc.file.name)
                      : doc.file_name
                        ? truncateFileName(doc.file_name)
                        : source === "google_drive"
                          ? "Google Drive"
                          : source === "docsend"
                            ? "DocSend"
                            : "Local upload";

                    return (
                      <div
                        key={`${doc.label}-${doc.file?.name || "uploaded"}-${index}`}
                        className="flex items-center justify-between p-3 border rounded-lg bg-card"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div
                            className={`rounded-lg ${fileConfig.bgColor} p-2 flex-shrink-0`}
                          >
                            <Icon className={`h-5 w-5 ${fileConfig.color}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium truncate">
                                {doc.label}
                              </p>
                              {hasMultipleVersions && (
                                <Badge variant="outline" className="text-xs">
                                  {filesOfThisType.length} versions
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <span
                                className="truncate max-w-[150px] sm:max-w-[180px]"
                                title={doc.file ? doc.file.name : undefined}
                              >
                                {fileNameToShow}
                              </span>

                              <span className="hidden sm:inline">•</span>
                              <span className="inline-flex items-center gap-1">
                                {source === "google_drive" ? (
                                  <>
                                    <GoogleDriveIcon className="h-3 w-3" />
                                    <span>Google Drive</span>
                                  </>
                                ) : source === "docsend" ? (
                                  <>
                                    <DocSendIcon className="h-3 w-3" />
                                    <span>DocSend</span>
                                  </>
                                ) : (
                                  <>
                                    <Upload className="h-3 w-3 text-muted-foreground/70" />
                                    <span>Local</span>
                                  </>
                                )}
                              </span>

                              {doc.time_stamp && (
                                <>
                                  <span className="hidden sm:inline">•</span>
                                  <span className="hidden sm:inline">
                                    {formatDate(doc.time_stamp)}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {doc.uploaded && (
                            <CheckCircle2 className="h-5 w-5 text-raising" />
                          )}
                          {!submitting && (
                            <>
                              {(doc.file || doc.file_url) && !isDevS3File(doc.file_url) && (
                                <Dialog>
                                  <DialogTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0"
                                    >
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent className="max-w-4xl h-[80vh] p-0 overflow-hidden">
                                    <div className="flex h-full w-full flex-col overflow-hidden">
                                      <div className="flex items-center gap-2 border-b bg-card px-4 py-2 shrink-0 pr-10">
                                        <h3 className="truncate text-sm font-semibold">
                                          {getFileName(doc)}
                                        </h3>
                                      </div>
                                    <div className="relative w-full flex-1 overflow-hidden">
                                      {(() => {
                                        const viewUrl = getViewUrl(doc);
                                        const fileName = getFileName(doc);
                                        const fileType = doc.file_type;

                                        if (!viewUrl) return null;

                                        if (
                                          isPDF(fileName, fileType, viewUrl)
                                        ) {
                                          return (
                                            <>
                                              <div className="absolute inset-0 flex items-center justify-center bg-card">
                                                <Loader2 className="h-8 w-8 animate-spin" />
                                              </div>
                                              <iframe
                                                src={
                                                  doc.file
                                                    ? viewUrl
                                                    : `https://docs.google.com/viewer?url=${encodeURIComponent(
                                                      viewUrl
                                                    )}&embedded=true`
                                                }
                                                className="w-full h-full"
                                                title={fileName}
                                                onLoad={(e) => {
                                                  const iframe =
                                                    e.target as HTMLIFrameElement;
                                                  const loader =
                                                    iframe.previousSibling as HTMLDivElement;
                                                  if (loader)
                                                    loader.style.display =
                                                      "none";
                                                }}
                                              />
                                            </>
                                          );
                                        } else if (
                                          isCSV(fileName, fileType, viewUrl) ||
                                          isExcel(fileName, fileType, viewUrl)
                                        ) {
                                          return (
                                            <SpreadsheetViewer
                                              url={viewUrl}
                                              fileName={fileName}
                                              fileId={doc._id}
                                            />
                                          );
                                        } else if (
                                          isPowerPoint(
                                            fileName,
                                            fileType,
                                            viewUrl
                                          )
                                        ) {
                                          return (
                                            <>
                                              <div className="absolute inset-0 flex items-center justify-center bg-card">
                                                <Loader2 className="h-8 w-8 animate-spin" />
                                              </div>
                                              <iframe
                                                src={
                                                  doc.file
                                                    ? viewUrl
                                                    : `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                                                      viewUrl
                                                    )}`
                                                }
                                                className="w-full h-full"
                                                title={fileName}
                                                onLoad={(e) => {
                                                  const iframe =
                                                    e.target as HTMLIFrameElement;
                                                  const loader =
                                                    iframe.previousSibling as HTMLDivElement;
                                                  if (loader)
                                                    loader.style.display =
                                                      "none";
                                                }}
                                              />
                                            </>
                                          );
                                        } else if (
                                          isImage(fileName, fileType, viewUrl)
                                        ) {
                                          return (
                                            <div className="flex items-center justify-center w-full h-full overflow-hidden p-2">
                                              <img
                                                src={viewUrl}
                                                alt={fileName}
                                                className="max-w-full max-h-full object-contain"
                                              />
                                            </div>
                                          );
                                        } else {
                                          return (
                                            <iframe
                                              src={viewUrl}
                                              className="w-full h-full"
                                              title={fileName}
                                            />
                                          );
                                        }
                                      })()}
                                    </div>
                                    </div>
                                  </DialogContent>
                                </Dialog>
                              )}

                              {/* History Button */}
                              {hasMultipleVersions && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={() =>
                                          handleShowHistory(doc.file_type)
                                        }
                                      >
                                        <History className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>
                                        View file history (
                                        {filesOfThisType.length} versions)
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}

                              {/* Delete Button */}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/5"
                                onClick={() => setFileToDelete(doc)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </div>

          {errors.mandatory && (
            <div className="p-3 bg-destructive/5 border border-destructive/30 rounded-md">
              <p className="text-sm text-destructive">{errors.mandatory}</p>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex gap-4 pt-6 pb-2 mt-4 border-t border-border/70">
            {onBack && (
              <Button
                type="button"
                variant="outline"
                onClick={onBack}
                className="w-full"
              >
                Back
              </Button>
            )}
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full"
            >
              {submitting ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-card"></div>
                  Uploading...
                </div>
              ) : (
                "Continue"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <GoogleDrivePicker
        open={isGoogleDriveOpen}
        onClose={() => setIsGoogleDriveOpen(false)}
        companyId={companyData?._id || ""}
        opportunityId={opportunityId}
        onImportComplete={handleGoogleDriveImport}
      />

      <DocSendPicker
        open={isDocSendOpen}
        onClose={() => setIsDocSendOpen(false)}
        companyId={companyData?._id || ""}
        opportunityId={opportunityId}
        mode="import"
        onImportComplete={handleDocSendImport}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!fileToDelete}
        onOpenChange={() => setFileToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove "{fileToDelete?.label}"? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (fileToDelete) {
                  const index = documents.findIndex(
                    (doc) =>
                      doc.file_type === fileToDelete.file_type &&
                      doc._id === fileToDelete._id &&
                      doc.label === fileToDelete.label
                  );
                  if (index !== -1) {
                    // If document exists in database, delete it immediately
                    if (fileToDelete.existing) {
                      const success = await deleteFile(
                        companyData._id,
                        fileToDelete.file_type
                      );
                      if (success) {
                        toast.success(
                          `${fileToDelete.label} deleted successfully`
                        );
                        // Remove from filesToDelete array since we already deleted it
                        setFilesToDelete(
                          filesToDelete.filter(
                            (f) => f.file_type !== fileToDelete.file_type
                          )
                        );
                      } else {
                        toast.error(`Failed to delete ${fileToDelete.label}`);
                        setFileToDelete(null);
                        return; // Don't remove from UI if deletion failed
                      }
                    }
                    // Remove from UI
                    handleRemoveDocument(index);
                  }
                  setFileToDelete(null);
                }
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Version History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <div className="space-y-4">
            <div>
              <h3 className="text-[15px] font-semibold tracking-tight">
                File History - {selectedFileHistory[0]?.label}
              </h3>
              <p className="text-sm text-muted-foreground">
                Showing all versions of this file type, newest to oldest
              </p>
            </div>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {selectedFileHistory.map((doc, index) => {
                const fileConfig = getFileIcon(doc);
                const Icon = fileConfig.icon;
                const fileNameToShow = doc.file
                  ? doc.file.name
                  : doc.file_url
                    ? "Uploaded File"
                    : "Unknown";

                return (
                  <div
                    key={`${doc._id || index}-${doc.file_type}`}
                    className="flex items-center justify-between p-3 border rounded-lg bg-card"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div
                        className={`rounded-lg ${fileConfig.bgColor} p-2 flex-shrink-0`}
                      >
                        <Icon className={`h-5 w-5 ${fileConfig.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium truncate">
                            Version {selectedFileHistory.length - index}
                          </p>
                          {index === 0 && (
                            <Badge variant="secondary" className="text-xs">
                              Latest
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground truncate">
                          {fileNameToShow}
                        </p>
                        {doc.time_stamp && (
                          <p className="text-xs text-muted-foreground/70 mt-1">
                            Uploaded: {formatDate(doc.time_stamp)}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {doc.file_url && !isDevS3File(doc.file_url) && (
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-4xl h-[80vh] p-0 overflow-hidden">
                            <div className="flex h-full w-full flex-col overflow-hidden">
                              <div className="flex items-center gap-2 border-b bg-card px-4 py-2 shrink-0 pr-10">
                                <h3 className="truncate text-sm font-semibold">
                                  {getFileName(doc)}
                                </h3>
                              </div>
                            <div className="relative w-full flex-1 overflow-hidden">
                              {(() => {
                                const viewUrl = doc.file_url;
                                const fileName = getFileName(doc);

                                if (!viewUrl) return null;

                                if (isPDF(fileName)) {
                                  return (
                                    <>
                                      <div className="absolute inset-0 flex items-center justify-center bg-card">
                                        <Loader2 className="h-8 w-8 animate-spin" />
                                      </div>
                                      <iframe
                                        src={`https://docs.google.com/viewer?url=${encodeURIComponent(
                                          viewUrl
                                        )}&embedded=true`}
                                        className="w-full h-full"
                                        title={fileName}
                                        onLoad={(e) => {
                                          const iframe =
                                            e.target as HTMLIFrameElement;
                                          const loader =
                                            iframe.previousSibling as HTMLDivElement;
                                          if (loader)
                                            loader.style.display = "none";
                                        }}
                                      />
                                    </>
                                  );
                                } else if (isPowerPoint(fileName)) {
                                  return (
                                    <>
                                      <div className="absolute inset-0 flex items-center justify-center bg-card">
                                        <Loader2 className="h-8 w-8 animate-spin" />
                                      </div>
                                      <iframe
                                        src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                                          viewUrl
                                        )}`}
                                        className="w-full h-full"
                                        title={fileName}
                                        onLoad={(e) => {
                                          const iframe =
                                            e.target as HTMLIFrameElement;
                                          const loader =
                                            iframe.previousSibling as HTMLDivElement;
                                          if (loader)
                                            loader.style.display = "none";
                                        }}
                                      />
                                    </>
                                  );
                                } else if (
                                  isCSV(fileName, doc.file_type, viewUrl) ||
                                  isExcel(fileName, doc.file_type, viewUrl)
                                ) {
                                  // `fileName` here can fall back to
                                  // `${doc.label}.pdf` via getFileName when
                                  // the doc has no file / file_name, which
                                  // would miss real spreadsheets. Match the
                                  // main preview flow above by also checking
                                  // `doc.file_type` and the `viewUrl`
                                  // extension so history items render the
                                  // SpreadsheetViewer instead of falling
                                  // through to the generic iframe.
                                  return (
                                    <SpreadsheetViewer
                                      url={viewUrl}
                                      fileName={fileName}
                                      fileId={doc._id}
                                    />
                                  );
                                } else if (isImage(fileName)) {
                                  return (
                                    <div className="flex items-center justify-center w-full h-full">
                                      <img
                                        src={viewUrl}
                                        alt={fileName}
                                        className="w-[800px] h-[600px] object-contain"
                                      />
                                    </div>
                                  );
                                } else {
                                  return (
                                    <iframe
                                      src={viewUrl}
                                      className="w-full h-full"
                                      title={fileName}
                                    />
                                  );
                                }
                              })()}
                            </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
